import * as React from "react";
import { Skeleton } from "@/components/ui";
import { FitFrame } from "./FitFrame";

const THUMBS = 3;
const TILES = 4;
const ENTRIES = 3;

/**
 * The editor's layout while a design loads: the slide column, the grey stage with its header row and
 * backgrounds, and the Activity panel with its thread and composer, so the page doesn't jump when the
 * design arrives.
 */
export const EditorSkeleton: React.FC = () => (
  <div
    className="flex flex-1 min-h-0 flex-col gap-6 overflow-hidden lg:flex-row"
    role="status"
    aria-label="Loading design"
    aria-busy="true"
    data-testid="editor-skeleton"
  >
    <div className="grid min-w-0 grid-cols-1 gap-2 pb-4 lg:min-h-0 lg:flex-[3] lg:grid-cols-[10rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:gap-0">
      <div className="flex gap-1 overflow-hidden p-2 lg:flex-col lg:pt-16 lg:pr-3 lg:pl-2">
        {Array.from({ length: THUMBS }, (_, i) => (
          <div key={i} className="flex shrink-0 flex-col items-center gap-1.5 p-2 lg:pl-4">
            <Skeleton className="aspect-square w-28 rounded-xl" />
            <Skeleton className="h-3 w-12" />
          </div>
        ))}
      </div>

      <div className="flex min-w-0 flex-col rounded-2xl border border-slate-200 bg-slate-50 shadow-sm lg:min-h-0">
        <div className="flex min-h-12 items-center gap-2 px-4 pt-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="size-7" />
          <Skeleton className="size-7" />
          <Skeleton className="ml-auto h-7 w-32" />
        </div>
        <div className="flex min-w-0 flex-col gap-4 px-4 pt-2 pb-4 lg:min-h-0 lg:flex-1">
          <div className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
            <FitFrame aspectRatio="square_1_1" className="mx-auto max-w-xl">
              <Skeleton className="size-full rounded-xl" />
            </FitFrame>
          </div>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-40" />
            <div className="flex gap-3">
              {Array.from({ length: TILES }, (_, i) => (
                <Skeleton key={i} className="aspect-square w-16 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="flex w-full shrink-0 flex-col lg:w-[380px] lg:min-h-0 xl:w-[420px]">
      <div className="flex items-center justify-between gap-2 pb-3">
        <div className="text-sm font-semibold text-slate-800">Activity</div>
        <Skeleton className="h-8 w-28" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {Array.from({ length: ENTRIES }, (_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-7 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-3 w-5/6" />
            </div>
          </div>
        ))}
      </div>
      <Skeleton className="mt-3 mb-4 h-28 w-full rounded-2xl" />
    </div>
  </div>
);
